import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Image, LayoutChangeEvent, Platform } from 'react-native';
import { IEvent } from '@/types/admin/events';
import { ADMIN_COLORS } from '@/constants/admin';
import { LoadingSpinner } from '@/components';
import { LazyImage } from '@/components/common/admin/mobile/LazyImage';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useRemoteImageAspect } from '@/hooks/common/useRemoteImageAspect';

interface EventInfoTabProps {
  event: IEvent;
}

export const EventInfoTab: React.FC<EventInfoTabProps> = ({ event }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [imageAspect, setImageAspect] = useState<number | null>(null); // height / width

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Measure actual image aspect ratio using shared hook
  const measuredAspect = useRemoteImageAspect(event.photos?.[currentImageIndex]?.photoUrl || null);
  useEffect(() => {
    setImageAspect(measuredAspect);
  }, [measuredAspect]);

  const onImageContainerLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  };

  const minImageHeight = 200;
  const maxImageHeight = Platform.OS === 'web' ? 360 : 300;
  const computedImageHeight = (imageAspect && containerWidth)
    ? Math.min(maxImageHeight, Math.max(minImageHeight, Math.round(containerWidth * imageAspect)))
    : minImageHeight;
  const displayImageHeight = computedImageHeight;

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Images */}
      {event.photos && event.photos.length > 0 && (
        <View style={styles.imageSection} onLayout={onImageContainerLayout}>
          <View style={[styles.imageFrame, { height: displayImageHeight }]}>
            <LazyImage
              source={{ uri: event.photos?.[currentImageIndex]?.photoUrl || '' }}
              style={{
                width: '100%',
                height: displayImageHeight,
              }}
              resizeMode={Platform.OS === 'web' ? 'contain' : 'cover'}
              placeholder={
                <View style={[styles.imagePlaceholder, { height: displayImageHeight }] }>
                  <LoadingSpinner message="Loading image..." />
                </View>
              }
            />
          </View>
          {event.photos.length > 1 && (
            <View style={styles.imageNavigation}>
              <TouchableOpacity
                style={styles.imageNavButton}
                onPress={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}
                disabled={currentImageIndex === 0}
              >
                <Text style={styles.imageNavButtonText}>‹</Text>
              </TouchableOpacity>
              <Text style={styles.imageCounter}>
                {currentImageIndex + 1} / {event.photos.length}
              </Text>
              <TouchableOpacity
                style={styles.imageNavButton}
                onPress={() => setCurrentImageIndex(Math.min(event.photos.length - 1, currentImageIndex + 1))}
                disabled={currentImageIndex === event.photos.length - 1}
              >
                <Text style={styles.imageNavButtonText}>›</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}

      {/* Description */}
      <View style={styles.section}>
        <Text style={styles.description}>{event.description}</Text>
      </View>

      {/* Event Details */}
      <View style={styles.detailsContainer}>
        {/* Date */}
        <View style={styles.detailItem}>
          <View style={styles.detailIcon}>
            <SVG_COMPONENTS.CALENDAR width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
          </View>
          <View style={styles.detailContent}>
            <Text style={styles.detailLabel}>DATE</Text>
            <Text style={styles.detailValue}>{formatDate(event.startTime)}</Text>
          </View>
        </View>

        {/* Time */}
        <View style={styles.detailItem}>
          <View style={styles.detailIcon}>
            <SVG_COMPONENTS.CLOCK width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
          </View>
          <View style={styles.detailContent}>
            <Text style={styles.detailLabel}>TIME</Text>
            <Text style={styles.detailValue}>
              {formatTime(event.startTime)} - {formatTime(event.endTime)}
            </Text>
          </View>
        </View>

        {/* Location */}
        <View style={styles.detailItem}>
          <View style={styles.detailIcon}>
            <SVG_COMPONENTS.LOCATION width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
          </View>
          <View style={styles.detailContent}>
            <Text style={styles.detailLabel}>LOCATION</Text>
            <Text style={styles.detailValue}>{event.location}</Text>
          </View>
        </View>

        {/* RSVP Limit */}
        <View style={styles.detailItem}>
          <View style={styles.detailIcon}>
            <SVG_COMPONENTS.GROUP width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
          </View>
          <View style={styles.detailContent}>
            <Text style={styles.detailLabel}>RSVP LIMIT</Text>
            <Text style={styles.detailValue}>
              {event.rsvpLimit === 0 ? 'Unlimited' : event.rsvpLimit}
            </Text>
          </View>
        </View>
      </View>

      {/* Internal Notes */}
      {event.internalNotes && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Internal Notes</Text>
          <Text style={styles.internalNotes}>{event.internalNotes}</Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  imageSection: {
    // marginBottom: 8,
  },
  imageFrame: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  eventImage: {
    width: '100%',
    height: 200,
  },
  imagePlaceholder: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholderText: {
    fontSize: 40,
  },
  imageNavigation: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
  },
  imageNavButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 8,
  },
  imageNavButtonText: {
    fontSize: 18,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  imageCounter: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginHorizontal: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 12,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  detailsContainer: {
    marginBottom: 24,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  detailIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  detailContent: {
    flex: 1,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_TERTIARY,
    marginBottom: 4,
  },
  detailValue: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  internalNotes: {
    fontSize: 14,
    lineHeight: 20,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    padding: 12,
    borderRadius: 8,
  },
});
